import zod from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextFieldComponent from '../../../components/TextFieldComponent';
import { Grid2 } from '@mui/material';
import ButtonComponent from '../../../components/ButtonComponent';
import {
  useCreateEscolarManagerIntegrationAsync,
  useGetEscolarManagerIntegrationByIdAsync,
  useUpdateEscolarManagerIntegrationAsync,
} from '../../../services/IntegrationService/EscolarManagerService/hooks/useMutation';
import CheckboxComponent from '../../../components/CheckboxComponent';
import { useContext, useEffect } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import { EscolarManagerIntegrationFormSchema } from '../../../services/IntegrationService/EscolarManagerService/schemas';

interface EscolarManagerEditionFormProps {
  escolarManagerIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type EscolarManagerEditionFormInput = zod.infer<
  typeof EscolarManagerIntegrationFormSchema
>;

const EscolarManagerEditionForm = ({
  escolarManagerIdToUpdate,
  handleSaveForm,
}: EscolarManagerEditionFormProps) => {
  const { showSnackbar } = useContext(AppContext);
  const { control, handleSubmit, reset } =
    useForm<EscolarManagerEditionFormInput>({
      resolver: zodResolver(EscolarManagerIntegrationFormSchema),
      defaultValues: {
        name: '',
        description: '',
        isActive: true,
        queryToken: '',
        registrationToken: '',
      },
    });

  const {
    mutate: mutateCreateEscolarManagerIntegration,
    isLoading: isLoadingCreateEscolarManagerIntegration,
  } = useCreateEscolarManagerIntegrationAsync();
  const {
    mutate: mutateUpdateEscolarManagerIntegration,
    isLoading: isLoadingUpdateEscolarManagerIntegration,
  } = useUpdateEscolarManagerIntegrationAsync();
  const {
    mutateAsync: mutateGetEscolarManagerIntegrationByIdAsync,
    isLoading: isLoadingGetEscolarManagerIntegrationById,
  } = useGetEscolarManagerIntegrationByIdAsync();

  const handleSave = (data: EscolarManagerEditionFormInput) => {
    if (escolarManagerIdToUpdate) {
      mutateUpdateEscolarManagerIntegration(
        { id: escolarManagerIdToUpdate, data },
        {
          onSuccess: () => {
            saveIntegration(
              'Integração Escolar Manager atualizada com sucesso.',
            );
          },
        },
      );
      return;
    }
    mutateCreateEscolarManagerIntegration(data, {
      onSuccess: () => {
        saveIntegration('Integração Escolar Manager criada com sucesso.');
      },
    });
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  useEffect(() => {
    if (escolarManagerIdToUpdate) {
      mutateGetEscolarManagerIntegrationByIdAsync(
        escolarManagerIdToUpdate,
      ).then((r) => reset(r, { keepDefaultValues: true }));
    }
  }, [escolarManagerIdToUpdate]);

  return (
    <>
      <Grid2 container columnSpacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Nome"
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Token de Consulta"
              />
            )}
            name={'queryToken'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Token de Registro de Acesso"
              />
            )}
            name={'registrationToken'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Descrição"
              />
            )}
            name={'description'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'right' }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CheckboxComponent
                value={value}
                onChange={onChange}
                label="Ativo"
              />
            )}
            name={'isActive'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <ButtonComponent
            fullWidth
            onClick={handleSubmit(handleSave)}
            loading={
              isLoadingCreateEscolarManagerIntegration ||
              isLoadingGetEscolarManagerIntegrationById ||
              isLoadingUpdateEscolarManagerIntegration
            }
          >
            Salvar
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </>
  );
};

export default EscolarManagerEditionForm;
